import React, { useState } from 'react'
import { Pill } from '../../../../../shared/sso-workspace/pill'
import { Notice } from '../../../../../shared/sso-workspace/fleet-page'
import { CopyValue } from '../../../../../shared/sso-workspace/copy-value'
import { ProviderLogo } from '../../../../../shared/sso-workspace/provider-logo'
import { formatAgo, formatDateTime } from '../../../../../shared/sso-workspace/format'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { ScimActivity } from '../../../../../shared/scim/scim-activity'
import { useScimActions } from '../../../../../shared/scim/scim-dialogs'
import { ScimSetupGuide } from '../../../../../shared/scim/scim-setup-guide'
import { useScimLinks } from '../../../../../shared/scim/use-scim-links'
import { useImpersonating } from '../../../../../shared/scim/use-impersonating'
import {
  expiryText, GOOGLE_NOTE, linkActions, linkMethod, linkState, madeByText, METHOD_OAUTH, methodText, ORG_INTRO, PLAN_OFF_NOTE, READ_ONLY_IMPERSONATING,
  REMOVE_IN_IDP, tokenPrefixText
} from '../../../../../shared/scim/scim-words'
import '../../../../../shared/scim/scim.scss'

// Provisioning (SCIM) on the organization's Single sign-on page (spec R22):
// the organization's own links to non-public connections -- whether it owns
// the connection or EducationXR manages it (R2 and R10 make that safe) --
// from content's GET /scim/connections. Turn on (the token's expiry,
// shown once with its Tenant URL), rotate (a grace for the old token) and
// revoke are for a person with ORG_SSO update, on the SSO plan (the page
// itself is plan-gated), never while impersonating (content refuses it).
// Recent activity links each person to their member page. The Entra and
// Okta setup guide sits under the rows. Each live row says how the identity
// provider authenticates (spec R26: a Bearer token, or OAuth client
// credentials with its client ID) and offers the switch to the other.
//
// connections: the page's connection rows (auth's /org-sso), for each
// link's logo. enabled: ORG_SSO update.
//
// revokeOnly (R3: revoking never needs the plan): the organization's SSO
// plan is off, and the page hands in the links that still have a token
// (scim: useScimLinks' { links, failure, reload }). Each row then offers
// only Revoke token and its activity: no turn on, no rotate, no guide.
export const OrgSsoProvisioning = ({ connections, enabled, revokeOnly = false, scim }) => {
  const impersonating = useImpersonating()
  const own = useScimLinks({ enabled: !scim })
  const { links, failure, reload } = scim || own
  const [activityFor, setActivityFor] = useState(null) // the connection whose activity is open
  const actions = useScimActions({ staff: false, onChanged: reload })
  const permitted = { canEdit: enabled, impersonating, staff: false, planOff: revokeOnly }
  const iconOf = id => (connections || []).find(x => x.id === id) || {}

  return (
    <div className="d-grid gap-3">
      {!revokeOnly && <p className="m-0">{ORG_INTRO} <span className="sw-hint">{REMOVE_IN_IDP}</span></p>}
      {impersonating && enabled && <Notice tone="warn">{READ_ONLY_IMPERSONATING}</Notice>}

      {links === undefined && <p className="sw-hint m-0">Loading provisioning…</p>}
      {links === null &&
        <div role="alert">
          <p className="text-danger small m-0">Provisioning couldn't be loaded.</p>
          <ErrorReference error={failure} compact />
        </div>}
      {Array.isArray(links) && links.length === 0 &&
        <div className="sc-empty-state">
          <p className="m-0">No connection can provision yet. Provisioning works through your organization's own sign-in connections: once one is linked above, turn provisioning on for it here.</p>
        </div>}
      {Array.isArray(links) && links.length > 0 &&
        <ul className="sc-rows">
          {links.map(link => {
            const id = link.connection.id
            const state = linkState(link)
            const can = linkActions(link, permitted)
            const showActivity = activityFor === id
            const t = link.token
            const oauth = linkMethod(link) === METHOD_OAUTH ? link.oauth : null
            return (
              <li key={id} className="sc-row" data-testid={`scim-${id}`}>
                <ProviderLogo iconSVG={iconOf(id).iconSVG} iconSVGDark={iconOf(id).iconSVGDark} />
                <div className="sc-row-main">
                  <span className="fw-semibold">{link.connection.name}</span>
                  {t
                    ? <span className="scim-row-facts">
                      <span data-testid={`scim-method-${id}`}>{methodText(link)}</span>
                      {oauth
                        ? <span>Client ID <span className="sw-mono">{oauth.clientId}</span> · secret <span className="sw-mono">{tokenPrefixText(t)}</span>{madeByText(t) ? ` · made by ${madeByText(t)}` : ''}</span>
                        : <span>Token <span className="sw-mono">{tokenPrefixText(t)}</span>{madeByText(t) ? ` · made by ${madeByText(t)}` : ''}</span>}
                      <span>Expires {t.expiresAt ? <span title={formatDateTime(t.expiresAt)}>{expiryText(t)}</span> : <Pill tone="warn" title="A token that never expires works until someone revokes it.">Never</Pill>}</span>
                      <span>Last used {t.lastUsedAt ? <span title={formatDateTime(t.lastUsedAt)}>{formatAgo(t.lastUsedAt)}</span> : 'never'}</span>
                      <span>Last request {link.lastRequestAt ? <span title={formatDateTime(link.lastRequestAt)}>{formatAgo(link.lastRequestAt)}</span> : 'none yet'}</span>
                    </span>
                    : <span className="sw-hint">{state.note || 'Your identity provider isn\'t provisioning people yet.'}</span>}
                  {t && state.note && <span className="sw-hint">{state.note}</span>}
                  {link.connection.google && <span className="sw-hint">{GOOGLE_NOTE}</span>}
                  {can.planBlocked && !revokeOnly && <span className="sw-hint">{PLAN_OFF_NOTE}</span>}
                </div>
                <Pill tone={state.tone}>{state.label}</Pill>

                {link.baseURL && state.key !== 'public' && !revokeOnly &&
                  <div className="scim-row-panel" style={{ maxWidth: 720 }}>
                    <CopyValue id={`scim-base-${id}`} label="Tenant URL (SCIM base URL)" value={link.baseURL} />
                  </div>}

                <div className="scim-row-panel scim-row-actions">
                  {can.turnOn &&
                    <button type="button" className="btn btn-sm sw-btn-primary" onClick={() => actions.askTurnOn(link)}>
                      {state.key === 'expired' ? 'Turn on with a new token' : 'Turn on provisioning'}
                    </button>}
                  {can.rotate && <button type="button" className="btn btn-sm sw-btn" onClick={() => actions.askRotate(link)}>{oauth ? 'Rotate client secret' : 'Rotate token'}</button>}
                  {can.switchMethod &&
                    <button type="button" className="btn btn-sm sw-btn" onClick={() => actions.askSwitch(link)}>
                      {oauth ? 'Switch to a Bearer token' : 'Switch to OAuth'}
                    </button>}
                  {state.key !== 'public' &&
                    <button type="button" className="btn btn-sm sw-btn" aria-expanded={showActivity} onClick={() => setActivityFor(showActivity ? null : id)}>
                      {showActivity ? 'Hide activity' : 'Activity'}
                    </button>}
                  {can.revoke && <button type="button" className="btn btn-sm sw-btn-outline-danger" onClick={() => actions.askRevoke(link)}>Revoke token</button>}
                </div>

                {showActivity &&
                  <div className="scim-row-panel sw-inline-panel" role="region" aria-label={`Activity for ${link.connection.name}`}>
                    <ScimActivity providerID={id} personHref={userID => `/org/user/list/edit/${userID}`} />
                  </div>}
              </li>
            )
          })}
        </ul>}

      {!revokeOnly && <ScimSetupGuide />}
      {actions.dialogs}
    </div>
  )
}
